import type {
  OnboardingAccountProfile,
  OnboardingFacts,
  OnboardingPlan,
} from "@wakeloop/schemas/types";
import { buildOnboardingPlan } from "@wakeloop/start";
import { resolveWakeLoopProfileOrNull } from "../config/resolve-wakeloop-profile";
import type { WakeLoopProfile } from "../config/wakeloop-profiles-api";
import type { StartReadinessSnapshot } from "../start-local/readiness-types";

export type CliOnboardingPlan = {
  facts: OnboardingFacts;
  plan: OnboardingPlan;
};

export async function resolveCliSetupOnboardingPlan(input: {
  profile?: string;
  readiness?: StartReadinessSnapshot;
  deviceStatus?: OnboardingFacts["device"]["status"];
}): Promise<CliOnboardingPlan> {
  const profile = await resolveWakeLoopProfileOrNull({
    ...(input.profile ? { wakeLoopProfileId: input.profile } : {}),
    allowDefaultFallback: true,
    allowUnauthenticated: false,
    persistSelection: false,
  });
  if (!profile) {
    throw new Error("setup.plan requires a resolved WakeLoop profile.");
  }

  return buildCliOnboardingPlanForProfile({
    profile,
    deviceStatus:
      input.deviceStatus ??
      resolveOnboardingDeviceStatusFromStartReadiness(input.readiness),
  });
}

export function buildCliOnboardingPlanForProfile(input: {
  profile: WakeLoopProfile;
  accountProfiles?: readonly WakeLoopProfile[];
  deviceStatus?: OnboardingFacts["device"]["status"];
}): CliOnboardingPlan {
  const actingProfile = toOnboardingAccountProfile(input.profile);
  const accountProfiles = [
    actingProfile,
    ...(input.accountProfiles ?? [])
      .filter(
        (profile) =>
          profile.wakeLoopProfileId !== input.profile.wakeLoopProfileId
      )
      .map(toOnboardingAccountProfile),
  ];
  const facts: OnboardingFacts = {
    auth: {
      signedIn: true,
      userId: input.profile.ownerUserId,
      actingProfileId: input.profile.wakeLoopProfileId,
      actingDisplayName: input.profile.profileName,
    },
    accountProfiles,
    space: null,
    device: {
      status: input.deviceStatus ?? "unknown",
    },
    context: {
      kind: "none",
    },
  };

  return {
    facts,
    plan: buildOnboardingPlan(facts),
  };
}

export function resolveOnboardingDeviceStatusFromStartReadiness(
  readiness: StartReadinessSnapshot | undefined
): OnboardingFacts["device"]["status"] {
  const service = readiness?.service;
  if (!service) {
    return "unknown";
  }
  if (service.runtime?.status === "running") {
    return "connected";
  }
  if (service.runtime?.status === "stale") {
    return "connecting";
  }
  if (
    service.status === "not_installed" ||
    service.runtime?.status === "not_running"
  ) {
    return "not_connected";
  }
  if (
    service.status === "installed_current" ||
    service.status === "installed_outdated"
  ) {
    return "not_connected";
  }
  return "unknown";
}

function toOnboardingAccountProfile(
  profile: WakeLoopProfile
): OnboardingAccountProfile {
  if (profile.profileKind !== "human" && profile.profileKind !== "agent") {
    throw new Error(
      "setup.plan only supports human or agent WakeLoop profiles."
    );
  }

  return {
    profileId: profile.wakeLoopProfileId,
    kind: profile.profileKind,
    name: profile.profileName,
    active: profile.status === "active",
  };
}
